/**
 * Light or dark: the person's choice, saved in Flashsuite and announced by its menu as `fs-appearance`
 * (docs/INTEGRATION.md section 16), once when the menu loads and again when it changes. Auto follows the
 * device. The last choice is kept on this device so the next visit opens in it before anything draws
 * (index.html reads the same key).
 */
const KEY = "flashadz-mode";

type Appearance = "auto" | "light" | "dark";

const isAppearance = (v: unknown): v is Appearance => v === "auto" || v === "light" || v === "dark";

export function followAppearance(): void {
  const root = document.documentElement;
  const dark = matchMedia("(prefers-color-scheme: dark)");
  let look: Appearance = "auto";
  try {
    const kept = localStorage.getItem(KEY);
    if (isAppearance(kept)) look = kept;
  } catch {
    // Why: a browser that keeps nothing follows the device until the menu says otherwise.
  }
  const apply = () => {
    root.dataset.mode = look === "auto" ? (dark.matches ? "dark" : "light") : look;
    // Why: the phone's browser bar takes the page's ground colour, so it follows the mode too.
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", getComputedStyle(root).getPropertyValue("--ink").trim());
  };
  document.addEventListener("fs-appearance", (e) => {
    const next = (e as CustomEvent<{ appearance?: unknown }>).detail?.appearance;
    if (!isAppearance(next)) return;
    look = next;
    try {
      localStorage.setItem(KEY, look);
    } catch {
      // Why: the choice still applies for this visit.
    }
    apply();
  });
  dark.addEventListener("change", apply);
  apply();
}
